import { useTranslation } from 'react-i18next';
import { ownerGroupLabel } from '../ownerGroup';
import { useCurrentUser } from '../auth/CurrentUserProvider';
import { CopyButton } from './CopyButton';
import { FieldLabel } from './FieldLabel';
import type { Group, LanguageTarget, QrCode, QrCodeKind } from '../api/types';
import type { ProductSelection } from './ProductPicker';

export const PRODUCT_COUNTRIES = ['CA', 'US'];

export const TITLE_MAX_LENGTH = 200;

export interface QrCodeFormValues {
  ownerGroupId: string;
  title: string;
  kind: QrCodeKind;
  targetUrl?: string;
  product?: ProductSelection;
  languageTargets?: LanguageTarget[];
  /** A file code's file: the first one on create, a replacement while editing. */
  file?: File;
  slug?: string;
  isActive: boolean;
}

export interface QrCodeFormProps {
  /** Groups the user may create in. Ignored (and locked) when editing. */
  groups: Group[];
  qrCode?: QrCode;
  submitting?: boolean;
  onSubmit: (values: QrCodeFormValues) => void | Promise<void>;
  onCancel?: () => void;
}

/** What each template needs on top of the shared props: the switch the shell owns. */
export interface CodeFormProps extends QrCodeFormProps {
  kindSwitch?: React.ReactNode;
}

/** Exported so the detail page validates an edited destination exactly as this form validates a new one. */
export function isHttpUrl(candidate: string): boolean {
  try {
    const url = new URL(candidate);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export function ValidationBanner({ message }: { message: string | null }) {
  if (!message) {
    return null;
  }
  return (
    <div className="alert alert-danger d-flex gap-2" role="alert">
      <i className="pi pi-exclamation-circle mt-1" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

const KIND_ICON: Record<QrCodeKind, string> = { Destination: 'pi-link', Product: 'pi-box', File: 'pi-file' };
const KIND_LABEL: Record<QrCodeKind, string> = {
  Destination: 'qrCode.kindDestination',
  Product: 'qrCode.kindProduct',
  File: 'qrCode.kindFile',
};

export function kindLabelKey(kind: QrCodeKind): string {
  return KIND_LABEL[kind];
}

export function KindSwitch({
  kind,
  kinds,
  disabled,
  onChange,
}: {
  kind: QrCodeKind;
  kinds: QrCodeKind[];
  disabled: boolean;
  onChange: (kind: QrCodeKind) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="field">
      <span className="form-label">
        <i className="pi pi-tag me-1" aria-hidden="true" />
        {t('qrCode.kindLabel')}
      </span>
      <div className="kind-switch" role="radiogroup" aria-label={t('qrCode.kindLabel')}>
        {kinds.map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={kind === option}
            className={`kind-switch__option${kind === option ? ' is-active' : ''}`}
            disabled={disabled}
            onClick={() => onChange(option)}
          >
            <i className={`pi ${KIND_ICON[option]} me-2`} aria-hidden="true" />
            {t(KIND_LABEL[option])}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The owning group: a choice while the code is being created, a fact once it exists. */
export function GroupField({
  groups,
  qrCode,
  value,
  disabled,
  onChange,
  kindSwitch,
}: {
  groups: Group[];
  qrCode?: QrCode;
  value: string;
  disabled: boolean;
  onChange: (groupId: string) => void;
  kindSwitch?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();

  if (qrCode) {
    return (
      <LockedValue
        label={t('common.group')}
        icon="pi-users"
        value={ownerGroupLabel(qrCode, t, user?.personalSpaceId)}
        hint={t('qrCode.groupLocked')}
      />
    );
  }

  return (
    <div className="field-row">
      <div className="field field-row__grow">
        <FieldLabel htmlFor="qr-group" icon="pi-users" required>
          {t('common.group')}
        </FieldLabel>
        <select
          id="qr-group"
          className="form-select"
          aria-required="true"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        >
          {groups.length === 0 && <option value="">{t('qrCode.noGroupAvailable')}</option>}
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
      </div>

      {kindSwitch}
    </div>
  );
}

export function ActiveToggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (active: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="form-check form-switch">
      <input
        id="qr-active"
        type="checkbox"
        role="switch"
        className="form-check-input"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <label className="form-check-label" htmlFor="qr-active">
        {t('qrCode.activeToggle')}
      </label>
    </div>
  );
}

export function FormActions({
  submitting,
  isEditing,
  onCancel,
}: {
  submitting: boolean;
  isEditing: boolean;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="form-actions">
      <button type="submit" className="btn btn-primary" disabled={submitting}>
        <i className={`pi ${submitting ? 'pi-spinner' : 'pi-check'} me-2`} aria-hidden="true" />
        {submitting ? t('common.saving') : isEditing ? t('common.save') : t('qrCode.create')}
      </button>
      {onCancel && (
        <button type="button" className="btn btn-light" onClick={onCancel} disabled={submitting}>
          <i className="pi pi-times me-2" aria-hidden="true" />
          {t('common.cancel')}
        </button>
      )}
    </div>
  );
}

/**
 * A value that is part of the record rather than a field waiting to be filled in. Deliberately not an
 * `<input readOnly>`: a text box that cannot be typed into invites clicking, and says nothing about why.
 */
export function LockedValue({
  label,
  icon,
  value,
  hint,
  mono = false,
  copyable = false,
}: {
  label: string;
  icon: string;
  value: string;
  hint: string;
  mono?: boolean;
  copyable?: boolean;
}) {
  return (
    <div className="field">
      <span className="form-label">
        <i className={`pi ${icon} me-1`} aria-hidden="true" />
        {label}
      </span>
      <div className="locked-value">
        <i className="pi pi-lock" aria-hidden="true" />
        <span className={mono ? 'mono' : undefined}>{value}</span>
        {copyable && <CopyButton value={value} />}
      </div>
      <span className="hint">{hint}</span>
    </div>
  );
}
